import { useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { CheckCircle2, Loader2, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const eur = (v) => (v == null ? null : `${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const fmtDay = (d) => {
  const iso = d.includes("-") ? d : `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
  return new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
};

export const useWins = () => useSWR("wins", () => api.get("/wins").then((r) => r.data), { revalidateOnFocus: false });

function PhotoCard({ p, onOpen }) {
  return (
    <button onClick={() => onOpen(p)} data-testid={`win-photo-card-${p.id}`}
      className="surface-raised text-left overflow-hidden border border-[#27272A] hover:border-[#39FF14] transition-colors flex flex-col">
      <div className="aspect-[4/5] overflow-hidden bg-black">
        <img src={p.images[0]} alt={p.caption || "Schedina vinta"} loading="lazy" className="w-full h-full object-cover object-top" />
      </div>
      <div className="p-4 space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          {p.odds != null && <span className="mono font-black text-[#39FF14] text-xl">Q {Number(p.odds).toFixed(2)}</span>}
          {p.date && <span className="text-[10px] mono text-[#71717A]">{fmtDay(p.date)}</span>}
        </div>
        {p.payout != null && (
          <div className="heading font-black text-2xl text-[#FFCC00] underline decoration-[#39FF14] decoration-2 underline-offset-4">
            {eur(p.payout)} vinti{p.stake != null && <span className="text-xs text-[#A1A1AA] no-underline font-normal"> con {eur(p.stake)}</span>}
          </div>
        )}
        {p.caption && <p className="text-xs text-[#A1A1AA] line-clamp-2">{p.caption}</p>}
      </div>
    </button>
  );
}

function SlipWin({ s }) {
  return (
    <div className="surface-raised p-5 border-t-2 border-[#39FF14]" data-testid={`win-slip-${s.date}-${s.id || s.target}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="label text-[10px]">{s.label || `Schedina quota ${s.target}`}</div>
        <span className="text-[10px] mono text-[#71717A]">{fmtDay(s.date)}</span>
      </div>
      <div className="flex items-end gap-4 mt-3">
        <div className="heading text-4xl font-black mono text-[#39FF14]">{s.total_odds.toFixed(2)}</div>
        <div className="text-xs text-[#A1A1AA] pb-1">{s.selections.length} eventi · vinta</div>
      </div>
      {s.payout != null && (
        <div className="heading font-black text-xl text-[#FFCC00] mt-2" data-testid={`win-slip-payout-${s.id}`}>{eur(s.payout)} vinti <span className="text-xs text-[#A1A1AA] font-normal">con {eur(s.stake)}</span></div>
      )}
      <ul className="mt-4 space-y-1.5">
        {s.selections.map((x) => (
          <li key={x.match_id} className="flex items-center gap-2 text-xs">
            <CheckCircle2 size={12} className="text-[#39FF14] shrink-0" />
            <span className="truncate flex-1">{x.home} - {x.away}</span>
            <span className="mono text-[#A1A1AA] shrink-0">{x.market_label}</span>
            {x.final_score && <span className="mono text-[#71717A] shrink-0 hidden sm:inline">{x.final_score}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function WinsGallery({ limit }) {
  const { data, isLoading } = useWins();
  const [open, setOpen] = useState(null);
  if (isLoading) return <Loader2 className="animate-spin text-[#A1A1AA]" />;
  if (!data) return null;
  const photos = limit ? data.photos.slice(0, limit) : data.photos;
  const won = limit ? data.slips.slice(0, limit) : data.slips;
  return (
    <section className="space-y-6" data-testid="wins-gallery">
      <div className="flex items-end justify-between gap-4">
        <div className="label flex items-center gap-2 text-[#FFCC00]"><Trophy size={12} /> Galleria vincite</div>
        {limit && (
          <Link to="/vincite" data-testid="wins-gallery-all-link" className="text-xs font-bold text-[#39FF14] hover:text-white transition-colors">
            Vedi tutte le vincite →
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {photos.map((p) => <PhotoCard key={p.id} p={p} onOpen={setOpen} />)}
      </div>
      {won.length > 0 && (
        <>
          <div className="label text-[10px]">Schedine dell&apos;app vinte · ultimi 60 giorni ({data.slips.length})</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {won.map((s) => <SlipWin key={`${s.date}-${s.id || s.target}`} s={s} />)}
          </div>
        </>
      )}
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-md p-0 bg-black border-[#27272A] max-h-[90vh] overflow-y-auto" data-testid="win-gallery-dialog">
          <DialogTitle className="sr-only">{open?.caption || "Schedina vinta"}</DialogTitle>
          {open?.caption && <p className="p-4 text-sm">{open.caption}</p>}
          {open?.images.map((src) => <img key={src} src={src} alt={open.caption || "Schedina vinta"} className="w-full" />)}
        </DialogContent>
      </Dialog>
    </section>
  );
}
